import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";

import { useCasesQuery, useProgressQuery, useWorklistQuery } from "../../hooks/useLabelingQueries";
import { FaChevronRight, FaFileAlt } from "../../utils/icons";

function isAiOutput(mode: string | undefined): boolean {
  return mode === "model" || mode === "ai";
}

function statusLabel(status: string | undefined): string {
  if (status === "complete") return "Complete";
  if (status === "partial") return "In progress";
  return "Not started";
}

export function SegmentationStudyHome() {
  const navigate = useNavigate();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const worklistQuery = useWorklistQuery(projectId);
  const casesQuery = useCasesQuery(projectId);
  const progressQuery = useProgressQuery(projectId);

  const studies = useMemo(() => worklistQuery.data?.studies ?? [], [worklistQuery.data]);
  const casesById = useMemo(
    () => new Map((casesQuery.data?.cases ?? []).map((item) => [item.case_id, item])),
    [casesQuery.data],
  );
  const aiCount = studies.filter((study) => isAiOutput(study.template_mode)).length;
  const blankCount = studies.length - aiCount;

  const handleOpen = (caseIndex: number) => {
    navigate(`/labeling?project=${encodeURIComponent(projectId)}&case=${caseIndex}`);
  };

  if (worklistQuery.isLoading) {
    return (
      <div className="h-full overflow-auto flex items-center justify-center page-premium-alt">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-fg-muted text-sm">Loading worklist...</span>
        </div>
      </div>
    );
  }

  if (worklistQuery.error || !worklistQuery.data) {
    return (
      <div className="h-full overflow-auto flex items-center justify-center page-premium-alt">
        <div className="bg-warn-tint border border-warn/30 rounded p-6 max-w-md text-center">
          <div className="text-warn font-medium mb-2">Failed to load worklist</div>
          <div className="text-warn/60 text-sm">
            {String(worklistQuery.error ?? "Unknown error")}
          </div>
        </div>
      </div>
    );
  }

  const worklist = worklistQuery.data;
  const completed = progressQuery.data?.completed_labels ?? 0;

  return (
    <>
      <Helmet>
        <title>{worklist.name} | a2z</title>
      </Helmet>

      <div className="h-full overflow-auto text-white page-premium">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            type="button"
            onClick={() => navigate("/labeling-overview/segmentation-studies")}
            className="mb-4 text-xs text-fg-muted transition hover:text-fg"
          >
            ← Segmentation Studies
          </button>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white">{worklist.name}</h1>
            <div className="flex items-center gap-4 mt-3 text-xs text-fg-muted">
              <span>{studies.length} cases</span>
              <span>|</span>
              <span>{aiCount} AI Output</span>
              <span>|</span>
              <span>{blankCount} Blank</span>
              <span>|</span>
              <span>{completed} complete</span>
            </div>
          </div>

          <div className="panel-premium rounded border border-border/50 divide-y divide-border/50">
            {studies.map((study, idx) => {
              const caseStatus = casesById.get(study.case_id)?.completion_status;
              const hasAiOutput = isAiOutput(study.template_mode);
              const primaryLabel = study.study_description || `Case ${idx + 1}`;

              return (
                <button
                  key={study.case_id}
                  type="button"
                  onClick={() => handleOpen(idx)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/2 transition text-left"
                >
                  <div className="w-8 text-fg-muted text-sm font-mono">{idx + 1}</div>
                  <FaFileAlt className="w-4 h-4 text-fg-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-fg truncate">{primaryLabel}</div>
                    <div className="text-xs text-fg-muted truncate">{study.study_instance_uid}</div>
                  </div>
                  <span className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded border border-border/60 bg-surface-2 text-fg-muted">
                    {statusLabel(caseStatus)}
                  </span>
                  <span
                    className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded ${
                      hasAiOutput
                        ? "bg-primary/15 text-signal-text-tint"
                        : "bg-surface-2 text-fg-muted"
                    }`}
                  >
                    {hasAiOutput ? "AI Output" : "Blank"}
                  </span>
                  <FaChevronRight className="w-3 h-3 text-fg-muted" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default SegmentationStudyHome;
